import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiArrowRight, FiGlobe, FiLoader, FiMapPin, FiMessageCircle, FiRadio,
} from 'react-icons/fi';
import { cUser, cUserPosts } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import FollowButton from './FollowButton';
import UserAvatar from './UserAvatar';
import UserBadges from './UserBadges';
import Linkify from './Linkify';
import { BLUE, BLUE_GRADIENT, compactNumber } from './brand';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * The **full profile of the member who wrote the article**, shown in the right
 * sidebar of the post detail page: cover, avatar, name, bio, location, website,
 * counts (articles / followers / following), Follow + Message and their latest
 * articles. While the full profile loads it falls back to the `author` object
 * that is already inside the post.
 */
export default function AuthorProfileCard({ userId, author = null }) {
  const { t } = useLanguage();
  const { user: me, requireAuth } = useCommunity();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(author);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  // The article was published “as a channel”: the channel is the author, so the
  // member behind it is never loaded or shown here.
  const isChannel = !!(author && author.channel);

  useEffect(() => {
    let alive = true;
    const id = userId || author?.id;
    if (isChannel) { setLoading(false); return undefined; }
    if (!id) { setLoading(false); return undefined; }
    setLoading(true);
    cUser(id)
      .then((full) => {
        if (!alive) return null;
        setProfile(full);
        return cUserPosts(id, { limit: 3 });
      })
      .then((res) => { if (alive && res) setPosts(res.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, author, isChannel]);

  if (!profile) return null;

  // ── published as a channel: show the channel, never the member ────────────
  if (isChannel) {
    const chUrl = `/channel/${profile.channel_slug || profile.channel_id}`;
    const chCover = profile.cover
      ? `url(${profile.cover.startsWith('http') ? profile.cover : `${API_BASE}${profile.cover}`})`
      : BLUE_GRADIENT;
    return (
      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        <div className="h-24 bg-cover bg-center" style={{ backgroundImage: chCover }} />
        <div className="px-4 pb-4">
          <div className="flex items-end gap-3 -mt-9">
            <UserAvatar user={profile} size={68} ring />
            <div className="min-w-0 flex-1 pb-0.5">
              <h3 className="font-extrabold text-sm truncate flex items-center gap-1.5">
                <span className="truncate">{profile.name || profile.username}</span>
                <UserBadges user={profile} />
              </h3>
              <p className="text-[11px] text-gray-500 truncate">
                @{profile.username} · {t('channelPostBadge')}
              </p>
            </div>
          </div>
          {profile.bio && (
            <p className="mt-2.5 text-xs text-gray-600 dark:text-gray-300">{profile.bio}</p>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
            <span><b>{compactNumber(profile.subscriber_count || 0)}</b> {t('subscribers')}</span>
            <span><b>{compactNumber(profile.post_count || 0)}</b> {t('articles')}</span>
          </div>
          <Link
            to={chUrl}
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-xs font-bold"
            style={{ background: BLUE }}
          >
            <FiRadio className="w-3.5 h-3.5" /> {t('channelsNav')}
          </Link>
        </div>
      </section>
    );
  }

  const isMe = me && me.id === profile.id;
  const joined = profile.created_at ? new Date(profile.created_at) : null;
  const cover = profile.cover
    ? `url(${profile.cover.startsWith('http') ? profile.cover : `${API_BASE}${profile.cover}`})`
    : 'linear-gradient(135deg,#0B5FDB,#083E93)';

  return (
    <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
      {/* cover + avatar */}
      <div className="h-24 bg-cover bg-center" style={{ backgroundImage: cover }} />
      <div className="px-4 pb-4">
        <div className="flex items-end gap-3 -mt-9">
          <UserAvatar user={profile} size={68} ring />
          <div className="min-w-0 flex-1 pb-0.5">
            <h3 className="font-extrabold text-sm truncate flex items-center gap-1.5">
              <span className="truncate">{profile.name || profile.username}</span>
              <UserBadges user={profile} />
            </h3>
            <p className="text-[11px] text-gray-500">@{profile.username}</p>
          </div>
        </div>

        {profile.bio && (
          <div className="mt-2.5">
            <Linkify text={profile.bio} className="text-xs text-gray-600 dark:text-gray-300" clamp={4} />
          </div>
        )}

        {(profile.city || profile.country || profile.website) && (
          <div className="mt-2 space-y-1 text-[11px] text-gray-500">
            {(profile.city || profile.country) && (
              <p className="inline-flex items-center gap-1.5">
                <FiMapPin className="w-3.5 h-3.5" />
                {[profile.city, profile.country].filter(Boolean).join(', ')}
              </p>
            )}
            {profile.website && (
              <p className="truncate">
                <a
                  href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 hover:underline"
                >
                  <FiGlobe className="w-3.5 h-3.5" /> {profile.website}
                </a>
              </p>
            )}
          </div>
        )}

        {/* counts */}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 py-2">
            <p className="text-sm font-extrabold">{compactNumber(profile.posts_count || 0)}</p>
            <p className="text-[10px] text-gray-500">{t('article')}</p>
          </div>
          <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 py-2">
            <p className="text-sm font-extrabold">{compactNumber(profile.followers_count || 0)}</p>
            <p className="text-[10px] text-gray-500">{t('followers')}</p>
          </div>
          <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 py-2">
            <p className="text-sm font-extrabold">{compactNumber(profile.following_count || 0)}</p>
            <p className="text-[10px] text-gray-500">{t('following')}</p>
          </div>
        </div>

        {/* actions */}
        <div className="mt-3 flex items-center gap-2">
          {isMe ? (
            <Link
              to="/me"
              className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-white text-xs font-bold"
              style={{ background: BLUE }}
            >
              {t('editProfile')}
            </Link>
          ) : (
            <>
              <div className="flex-1">
                <FollowButton user={profile} block />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!requireAuth(t('loginRequiredDesc'))) return;
                  navigate(`/messages/${profile.id}`);
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-bold text-white"
                style={{ background: BLUE }}
              >
                <FiMessageCircle className="w-3.5 h-3.5" /> {t('message')}
              </button>
            </>
          )}
        </div>

        <Link
          to={`/u/${profile.username}`}
          className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold hover:underline"
          style={{ color: BLUE }}
        >
          {t('viewProfile')} <FiArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* their latest articles */}
      <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3">
        <h4 className="text-[11px] font-extrabold uppercase tracking-wide text-gray-400 mb-2">
          {t('moreFromAuthor')}
        </h4>
        {loading && posts.length === 0 && (
          <p className="text-[11px] text-gray-400 inline-flex items-center gap-1.5">
            <FiLoader className="w-3.5 h-3.5 animate-spin" /> {t('loading')}
          </p>
        )}
        <div className="space-y-2">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/post/${p.id}`}
              className="block text-xs font-semibold leading-snug hover:underline"
            >
              {p.title || p.description || '…'}
            </Link>
          ))}
          {!loading && posts.length === 0 && (
            <p className="text-[11px] text-gray-400">{t('noPosts')}</p>
          )}
        </div>
        {joined && (
          <p className="mt-3 text-[10px] text-gray-400">
            {t('joined')} {joined.getFullYear()}
          </p>
        )}
      </div>
    </section>
  );
}
